import { useLazyQuery } from "@apollo/client";
import { IonContent, IonInfiniteScroll, IonInfiniteScrollContent } from "@ionic/react";
import { isToday, isYesterday } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ClientAvatar from "@components/ClientAvatar";
import DateStamp from "@components/dates/DateStamp";
import ExpertAvatar from "@components/ExpertAvatar";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import { InboxContext } from "@components/messaging/InboxProvider";
import Message from "@components/messaging/Message";
import styles from "@components/messaging/Messages.module.css";
import Typing from "@components/messaging/Typing";
import useSubscribeToMessageTypers from "@hooks/subscriptions/useSubscribeToMessageTypers";
import useContextSafely from "@hooks/useContextSafely";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  ChatFragment,
  ChatMessagesMarkReadDocument,
  ChatMessagesMarkReadMutation,
  ChatMessagesMarkReadMutationVariables,
  GetChatMessagesDocument,
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
  GetJourneyForMessagesQuery,
  MessageFragment
} from "@typing/Generated";
import { Colors } from "@utils/colorUtils";
import { groupByTimestampDay, modelWithUniqueIds } from "@utils/modelUtils";

interface MessageRefs {
  [key: string]: HTMLDivElement | null;
}

type Props = {
  chat: ChatFragment;
  chatUsers: {
    avatarProps: {
      color?: Colors;
      expert?: NonNullable<GetJourneyForMessagesQuery["journey"]["expertRoles"]>[number]["expert"];
      firstName?: string;
    };
    id: string;
  }[];
  messageContentRef: RefObject<HTMLIonContentElement>;
  shouldAnimate: boolean;
};

const ChatThread = ({ chat, chatUsers, messageContentRef, shouldAnimate }: Props) => {
  const intl = useIntl();
  const [messageRefs, setMessageRefs] = useState<MessageRefs>({});
  const messageTypers = useSubscribeToMessageTypers({ chatId: chat.id });
  const messageTyperUsers = chatUsers.filter(user => messageTypers.some(typer => typer.user?.id === user.id));
  const myExpert = useMyExpert();

  const { journey, messageSearch, setMessageSearch } = useContextSafely(InboxContext);

  const [getChatMessages, getChatMessagesQuery] = useLazyQuery<GetChatMessagesQuery, GetChatMessagesQueryVariables>(
    GetChatMessagesDocument
  );

  const [chatMessagesMarkRead] = useMutationWithValidation<
    ChatMessagesMarkReadMutation,
    ChatMessagesMarkReadMutationVariables
  >(ChatMessagesMarkReadDocument, "messages");

  const searchForMessage = useCallback(async () => {
    let message: MessageFragment | null =
      getChatMessagesQuery.data?.chatMessages.edges?.find(edge => edge?.node?.id === messageSearch.messageId)?.node ??
      null;

    let cursor = getChatMessagesQuery.data?.chatMessages.pageInfo?.endCursor ?? "";

    while (message === null) {
      const res = await getChatMessagesQuery.fetchMore({
        variables: { cursor, id: chat.id }
      });

      if (res.data?.chatMessages.edges) {
        cursor = res.data.chatMessages.pageInfo?.endCursor ?? "";
        message = res.data?.chatMessages.edges?.find(edge => edge?.node?.id === messageSearch.messageId)?.node ?? null;
      }
    }

    if (message) {
      setMessageSearch({ ...messageSearch, found: true });
    }
  }, [getChatMessagesQuery, messageSearch, setMessageSearch, chat.id]);

  useEffect(() => {
    if (messageSearch.found === false && messageSearch.messageId) {
      searchForMessage();
    }
  }, [
    getChatMessagesQuery.data?.chatMessages.pageInfo?.endCursor,
    messageSearch.found,
    messageSearch.messageId,
    searchForMessage
  ]);

  useEffect(() => {
    if (!getChatMessagesQuery.loading) setTimeout(() => messageContentRef.current?.scrollToBottom(), 300);
  }, [chat.id, getChatMessagesQuery.loading, messageContentRef]);

  const registerMessageRef = (messageId: string, ref: HTMLDivElement | null) => {
    setMessageRefs(prevMessageRefs => ({
      ...prevMessageRefs,
      [messageId]: ref
    }));
  };

  const getUserColor = (userId: string) => {
    const user = chatUsers.find(user => user.id === userId);

    if (user && "firstName" in user?.avatarProps) {
      return user?.avatarProps.color;
    }
  };

  useEffect(() => {
    getChatMessages({
      variables: { id: chat.id }
    });
  }, [chat.id, getChatMessages]);

  async function searchNext(event: CustomEvent<void>) {
    await getChatMessagesQuery
      .fetchMore({
        variables: { cursor: getChatMessagesQuery.data?.chatMessages.pageInfo?.endCursor ?? "", id: chat.id }
      })
      .then(() => (event.target as HTMLIonInfiniteScrollElement).complete());
  }

  const messages = useNodesFromConnection(getChatMessagesQuery.data?.chatMessages);

  const sortedMessagesByDay = useMemo(
    () => groupByTimestampDay<MessageFragment>(messages, message => message.createdAt, intl, true),
    [intl, messages]
  );

  useEffect(() => {
    messageContentRef.current?.scrollToBottom();
  }, [messages.length, messageContentRef]);

  useEffect(() => {
    if (messageSearch.found === true && messageSearch.messageId && messageRefs[messageSearch.messageId]) {
      const messageId = messageSearch.messageId;
      setTimeout(() => {
        messageRefs[messageId]?.scrollIntoView({ behavior: "auto", block: "center" });
      }, 500);
      setMessageSearch({ ...messageSearch, found: null });
    }
  }, [messageRefs, messageSearch, messageSearch.found, messageSearch.messageId, setMessageSearch]);

  useEffect(() => {
    if (!myExpert || journey?.expertRoles?.some(role => role.expert.id === myExpert?.id)) {
      chatMessagesMarkRead({
        id: chat.id
      });
    }
  }, [chat.id, chatMessagesMarkRead, journey?.expertRoles, myExpert]);

  return (
    <HandleQuery query={getChatMessagesQuery}>
      {() => (
        <div className={styles.scroll}>
          <IonContent ref={messageContentRef} scrollY>
            <IonInfiniteScroll
              disabled={getChatMessagesQuery.data?.chatMessages.pageInfo?.hasNextPage === false}
              onIonInfinite={searchNext}
              position="top"
              threshold="100px"
            >
              <IonInfiniteScrollContent />
            </IonInfiniteScroll>
            <AnimatePresence initial={false}>
              <div className={styles.chatMessages}>
                {sortedMessagesByDay.map(group => (
                  <div key={`${group.year}-${group.month}-${group.date}`}>
                    <div className={styles.chatMessagesDate}>
                      <div className={styles.chatLine} />
                      <Tag color="primary-500">
                        <div className="no-wrap">
                          {isToday(new Date(group.year, group.month, group.date)) ? (
                            <FormattedMessage id="dictionary.today" />
                          ) : isYesterday(new Date(group.year, group.month, group.date)) ? (
                            <FormattedMessage id="dictionary.yesterday" />
                          ) : (
                            <DateStamp
                              showYear={group.year !== new Date().getFullYear()}
                              value={new Date(group.year, group.month, group.date)}
                            />
                          )}
                        </div>
                      </Tag>
                      <div className={styles.chatLine} />
                    </div>
                    {modelWithUniqueIds(group.models).map((message, index) => {
                      const hideAvatar = index > 0 && message.author.id === group.models[index - 1].author.id;

                      return (
                        <motion.div
                          animate={{ opacity: 1, scale: 1 }}
                          initial={shouldAnimate ? { opacity: 0, scale: 0.8 } : false}
                          key={message.id}
                          ref={ref => registerMessageRef(message.id, ref)}
                          transition={{ opacity: { duration: 0.2 } }}
                        >
                          <Message
                            authorColor={getUserColor(message.author.id)}
                            authorOfPreviousMessage={hideAvatar}
                            journeyId={journey.id}
                            message={message}
                            messageSearch={messageSearch}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
                {messageTyperUsers.map(user => (
                  <motion.div
                    animate={{ opacity: 1, scale: 1 }}
                    initial={shouldAnimate ? { opacity: 0, scale: 0.8 } : false}
                    key={user.id}
                    ref={ref => registerMessageRef(`${user.id}-typing`, ref)}
                    transition={{ opacity: { duration: 0.2 } }}
                  >
                    <div className={styles.message} data-test="message">
                      <div>
                        <div className={styles.messageAvatarSpacing}>
                          <div className={styles.messageAvatar}>
                            {user.avatarProps.expert && <ExpertAvatar expert={user.avatarProps.expert} />}
                            {user.avatarProps.firstName && (
                              <ClientAvatar
                                clientId={user.id}
                                color={user.avatarProps.color}
                                firstName={user.avatarProps.firstName}
                                journeyId={journey.id}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className={styles.messageIncoming}>
                          <div className={styles.body}>
                            <Typing />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                <div className={styles.anchor}></div>
              </div>
            </AnimatePresence>
          </IonContent>
        </div>
      )}
    </HandleQuery>
  );
};

export default ChatThread;
